import { useState, useEffect, useRef } from 'react';
import { getTenant } from '../../utils/tenantHelper';
import { getWoodCutOptimizerConfig } from './utils';
import { EVENT_TYPES, ACTIONS } from './constants';
import { shouldShowCouchMark, incrementCouchMarkCount, disableCouchMark } from './couchMarkUtils';

const useWoodCutOptimizer = (handleAddToCart, product, appCtx) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCouchMark, setShowCouchMark] = useState(false);
  const [config, setConfig] = useState({ iframeSrcUrl: '', showButton: false });

  const buttonRef = useRef(null);
  const tooltipRef = useRef(null);
  const handleAddToCartRef = useRef(handleAddToCart);

  const { regionCode, politicalId, priceGroupId, zones, deviceType } = appCtx;

  useEffect(() => {
    handleAddToCartRef.current = handleAddToCart;
  }, [handleAddToCart]);

  useEffect(() => {
    const tenant = getTenant(regionCode);
    const { iframeSrcUrl, showButton } = getWoodCutOptimizerConfig(tenant, product, {
      politicalId,
      priceGroupId,
      zones,
    });
    setConfig({ iframeSrcUrl, showButton });
  }, [product, regionCode, politicalId, priceGroupId, zones]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideTooltip = tooltipRef.current?.contains(event.target);
      const isClickInsideButton = buttonRef.current?.contains(event.target);

      if (!isClickInsideTooltip && !isClickInsideButton) {
        setIsTooltipVisible(false);
        if (showCouchMark) {
          setShowCouchMark(false);
        }
      }
    };

    if (isTooltipVisible && config.showButton) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTooltipVisible, showCouchMark, config.showButton]);

  useEffect(() => {
    const handleMessage = (event) => {
      const eventInfo = event.data;
      if (eventInfo.eventType === EVENT_TYPES.WOOD_CUT_OPTIMIZER) {
        const { action, data } = eventInfo;
        if (action === ACTIONS.CLOSE_MODAL) {
          handleCloseModal();
        } else if (action === ACTIONS.ADD_TO_CART) {
          const { quantity, customInfo, customCartLineNumber } = data;
          handleCloseModal();
          handleAddToCartRef.current(quantity, customInfo, customCartLineNumber);
        }
      }
    };

    if (config.showButton) {
      window.addEventListener('message', handleMessage);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [config.showButton]);

  useEffect(() => {
    const isCouchMarkVisible = shouldShowCouchMark();
    if (isCouchMarkVisible && config.showButton) {
      setShowCouchMark(true);
      incrementCouchMarkCount();
    }
  }, [config.showButton]);

  const handleButtonClick = () => {
    setIsTooltipVisible(false);
    setShowCouchMark(false);
    handleOpenModal();
  };

  const handleTooltipButtonClick = (event) => {
    event.stopPropagation();
    if (showCouchMark) {
      disableCouchMark();
      setShowCouchMark(false);
    }
    setIsTooltipVisible(false);
  };

  const handleCouchMarkClick = (event) => {
    event.stopPropagation();
    setIsTooltipVisible(!isTooltipVisible);
  };

  return {
    isTooltipVisible,
    isModalOpen,
    buttonRef,
    tooltipRef,
    handleButtonClick,
    handleTooltipButtonClick,
    handleCloseModal,
    iframeSrcUrl: config.iframeSrcUrl,
    showButton: config.showButton,
    showCouchMark,
    handleCouchMarkClick,
    deviceType,
  };
};

export default useWoodCutOptimizer;
